import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";
import useSavUnreadCount from "@/lib/useSavUnreadCount";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  House, CalendarBlank, HardHat, FileText, Gear, Robot, Broadcast, MagnifyingGlass,
  Plus, ShieldStar, Bed, SignOut, Fire, MapTrifold, Wallet, Truck, ChatCircleDots,
} from "@phosphor-icons/react";

const TABS = [
  { to: "/", label: "Accueil", icon: House, end: true, hideForTech: true },
  { to: "/agenda", label: "Agenda", icon: CalendarBlank },
  { to: "/chantiers", label: "Chantiers", icon: HardHat },
  { to: "/messages", label: "Messages", icon: ChatCircleDots, badgeKey: "dm" },
  { to: "/alertes", label: "Alertes", icon: Fire, badgeKey: "sav" },
  { to: "/vehicule", label: "Mon véhicule", icon: Truck },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/rh", label: "Congés", icon: Bed },
  { to: "/reglages", label: "Réglages", icon: Gear },
];

const ADMIN_TABS = [
  { to: "/admin", label: "Console gérant", icon: ShieldStar },
  { to: "/heures", label: "Heures & km", icon: MapTrifold },
  // { to: "/payroll", label: "Paniers & Trajets", icon: Wallet },  // remplacé par /heures
];

/**
 * Persistent left sidebar for desktop (≥ lg). Never rendered on mobile.
 * Emits `onCreate` callback for the "+" primary CTA.
 */
export default function DesktopSidebar({ onCreate }) {
  const { user, isAdmin, logout } = useAuth();
  const nav = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isTech = user?.role === "technician";
  const visibleTabs = TABS.filter((t) => !(t.hideForTech && isTech));
  const tabs = [...visibleTabs, ...(isAdmin ? ADMIN_TABS : [])];
  const { count: savCount } = useSavUnreadCount();
  const [dmUnread, setDmUnread] = useState(0);

  useEffect(() => {
    const fetchDm = () => {
      api.get("/dm/summary")
        .then((r) => {
          const total = (r.data || []).reduce((sum, t) => sum + (t.unread || 0), 0);
          setDmUnread(total);
        })
        .catch(() => {});
    };
    fetchDm();
    const id = setInterval(fetchDm, 15000);
    return () => clearInterval(id);
  }, []);

  return (
    <aside
      data-testid="desktop-sidebar"
      className="hidden lg:flex flex-col w-64 shrink-0 h-screen sticky top-0 border-r border-white/10 bg-[#0A0A0A]/95 backdrop-blur-md z-30"
    >
      {/* Brand */}
      <button
        onClick={() => nav(isTech ? "/agenda" : "/")}
        className="flex items-center gap-3 px-5 py-5 border-b border-white/5 hover:bg-white/5 transition-colors"
        data-testid="sidebar-brand"
      >
        <div className="w-11 h-11 rounded-xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_20px_rgba(0,92,255,0.4)]">
          <span className="font-display font-extrabold text-lg leading-none">B</span>
        </div>
        <div className="text-left">
          <div className="font-display font-bold text-base">BKflow</div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Chantier Pro</div>
        </div>
      </button>

      {/* Primary create CTA */}
      <div className="px-4 pt-4">
        <button
          onClick={onCreate}
          className="w-full h-11 rounded-xl bg-[#005CFF] hover:bg-[#0047CC] flex items-center justify-center gap-2 font-semibold text-sm shadow-[0_10px_30px_-8px_rgba(0,92,255,0.6)] active:scale-95 transition-all"
          data-testid="sidebar-create-btn"
        >
          <Plus size={16} weight="bold" /> Nouveau
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5" data-testid="sidebar-nav">
        {tabs.map(({ to, label, icon: Icon, end, badgeKey }) => {
          const badge = badgeKey === "sav" ? savCount : badgeKey === "dm" ? dmUnread : 0;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`sidebar-tab-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 h-11 px-3 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-[#005CFF]/15 text-white border border-[#005CFF]/30"
                    : "text-white/60 hover:bg-white/5 hover:text-white border border-transparent"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={17} weight={isActive ? "fill" : "regular"} className={isActive ? "text-[#7CB2FF]" : ""} />
                  <span className="flex-1">{label}</span>
                  {badge > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`sidebar-badge-${label.toLowerCase()}`}>
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Utility row */}
      <div className="border-t border-white/5 p-3 grid grid-cols-4 gap-1.5">
        <button
          onClick={() => setSearchOpen(true)}
          className="h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70"
          title="Recherche (⌘K)"
          data-testid="sidebar-search"
        >
          <MagnifyingGlass size={15} />
        </button>
        <button
          onClick={() => nav("/fil")}
          className="h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70"
          title="Fil d'actualité"
        >
          <Broadcast size={15} />
        </button>
        <div className="h-9 rounded-lg bg-white/5 flex items-center justify-center">
          <NotificationBell />
        </div>
        <button
          onClick={() => nav("/ia")}
          className="h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/70"
          title="Assistant IA"
        >
          <Robot size={15} />
        </button>
      </div>

      {/* User card */}
      <div className="border-t border-white/5 p-3 flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#005CFF] to-[#0047CC] flex items-center justify-center text-xs font-bold shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate">{user?.name}</div>
          <div className="text-[9px] uppercase tracking-widest text-white/40">{user?.role}</div>
        </div>
        <button
          onClick={logout}
          className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 text-white/60 flex items-center justify-center transition-colors"
          title="Se déconnecter"
          data-testid="sidebar-logout"
        >
          <SignOut size={13} />
        </button>
      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </aside>
  );
}
